import { useState, useEffect, useRef } from 'react';

// Sticky acoplado del home. Tres piezas que se apilan en este orden:
//
//   1. Buscador flotante — releva al buscador del header en el momento exacto
//      en que ese sale por arriba, y se queda anclado.
//   2. Categorías — se pegan justo debajo del buscador.
//   3. Filtros — viven más abajo en el documento (debajo de la sección de
//      tendencias), con su propio sentinel. Se pegan bajo las categorías y solo
//      se esconden tras seguir bajando HIDE_FILTERS_AFTER px. Al subir vuelven.
//
// Cada sticky usa `top` con el alto medido del de arriba (nada de números
// mágicos ni transforms encadenados), así los tres quedan sin huecos.
//
// `scrollContainerRef` — opcional. Si se provee, escucha el scroll de ese
// container y lo usa como root de los IntersectionObserver. Sino, usa
// window/viewport. Necesario para arquitecturas tipo keep-alive donde cada
// sección tiene su propio scroll container.

// Px de scroll extra, después de que las categorías se pegan, antes de que los
// filtros empiecen a esconderse. Da la sensación de bloque único al principio.
const HIDE_FILTERS_AFTER = 160;

// Px de scroll durante los que el fondo del buscador va virando de la imagen
// del header a blanco. El recorrido termina justo cuando las categorías se
// pegan, así que a mitad de camino se ve translúcido.
const WHITE_FADE_RANGE = 90;

export function useStickyHeaderObservers({ scrollContainerRef } = {}) {
  const [categoriesStuck, setCategoriesStuck] = useState(false);
  // 0 = imagen del header, 1 = blanco pleno. Va atado al scroll, no a una
  // duración fija, para que la translucidez avance conforme bajas.
  const [searchWhite, setSearchWhite] = useState(0);
  const [filtersHidden, setFiltersHidden] = useState(false);
  const [filtersStuck, setFiltersStuck] = useState(false);
  const [fixedHeaderHeight, setFixedHeaderHeight] = useState(58);
  const [categoriesBarHeight, setCategoriesBarHeight] = useState(90);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [cartBarVisible, setCartBarVisible] = useState(true);

  const [headerCollapse, setHeaderCollapse] = useState(0);
  // true cuando el buscador ya quedó anclado arriba. Mientras no, la mitad de
  // abajo del buscador deja ver la sección promocional (el header solo llega
  // hasta la mitad del buscador); ya anclado, el header lo cubre completo
  // para que no se vea el contenido pasando por debajo.
  const [headerStuck, setHeaderStuck] = useState(false);
  const headerCollapseRef = useRef(0);
  headerCollapseRef.current = headerCollapse;

  const headerRef = useRef(null);
  const searchRowRef = useRef(null);
  const categoriesSentinelRef = useRef(null);
  const filtersSentinelRef = useRef(null);
  const categoriesBarRef = useRef(null);
  // Scroll en el que las categorías se pegaron, para medir desde ahí cuánto
  // falta para esconder los filtros.
  const stuckAtYRef = useRef(null);

  // Altos reales vía ResizeObserver: las dos barras cambian de alto (la de
  // categorías compacta sus chips al pegarse) y una medición puntual se queda
  // vieja, dejando un hueco por el que se ve pasar el contenido.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;

    // headerCollapse = cuánto del header queda por encima del buscador (safe
    // area + fila de ubicación). Es el `top` negativo con el que el header se
    // pega: esa parte se sale de vista y el buscador queda anclado.
    const measureHeader = () => {
      const header = headerRef.current;
      const row = searchRowRef.current;
      if (!header || !row) return;
      const collapse = row.getBoundingClientRect().top - header.getBoundingClientRect().top;
      if (collapse >= 0) setHeaderCollapse(Math.round(collapse));
      const h = row.offsetHeight;
      if (h) setFixedHeaderHeight(h);
    };

    const ro = new ResizeObserver(() => measureHeader());
    [headerRef.current, searchRowRef.current].filter(Boolean).forEach((el) => ro.observe(el));

    const roCats = new ResizeObserver(([entry]) => {
      const h = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.offsetHeight;
      if (h) setCategoriesBarHeight(h);
    });
    if (categoriesBarRef.current) {
      roCats.observe(categoriesBarRef.current);
      // Medida inicial síncrona: si el ResizeObserver tarda en su primer
      // aviso, los filtros se pegaban con el alto por defecto (90) y quedaba
      // un hueco entre ellos y las categorías.
      setCategoriesBarHeight(categoriesBarRef.current.offsetHeight || 90);
    }

    measureHeader();
    return () => { ro.disconnect(); roCats.disconnect(); };
  }, []);

  // Sentinel de categorías: dispara en la misma línea donde se pegan, es decir
  // debajo del buscador ya anclado.
  useEffect(() => {
    const sentinel = categoriesSentinelRef.current;
    if (!sentinel) return;
    const root = scrollContainerRef?.current || null;
    const observer = new IntersectionObserver(
      ([entry]) => setCategoriesStuck(!entry.isIntersecting),
      { root, rootMargin: `-${fixedHeaderHeight - 1}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollContainerRef, fixedHeaderHeight]);

  // Sentinel de los filtros: la línea donde se pegan es debajo del buscador
  // MÁS el alto de la barra de categorías.
  useEffect(() => {
    const sentinel = filtersSentinelRef.current;
    if (!sentinel) return;
    const root = scrollContainerRef?.current || null;
    const linea = fixedHeaderHeight - 1 + categoriesBarHeight - 1;
    const observer = new IntersectionObserver(
      ([entry]) => setFiltersStuck(!entry.isIntersecting),
      { root, rootMargin: `-${linea}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollContainerRef, fixedHeaderHeight, categoriesBarHeight]);

  // Marca desde qué scroll empezar a contar para esconder los filtros.
  //
  // Se ata a filtersStuck, no a categoriesStuck: los filtros ya no viven
  // pegados a las categorías en el DOM, así que las categorías se pegan mucho
  // antes. Contando desde ahí, los filtros empezaban a esconderse mientras
  // seguían en flujo normal — se veían "subirse solos" a media página.
  useEffect(() => {
    if (!filtersStuck) {
      stuckAtYRef.current = null;
      setFiltersHidden(false);
      return;
    }
    const container = scrollContainerRef?.current;
    stuckAtYRef.current = container ? container.scrollTop : window.scrollY;
  }, [filtersStuck, scrollContainerRef]);

  // Scroll listener: container.scrollTop si hay container, sino window.scrollY.
  useEffect(() => {
    const container = scrollContainerRef?.current;
    const getY = () => (container ? container.scrollTop : window.scrollY);
    let lastY = getY();
    let rafId = null;
    // Esconder/mostrar los filtros pide recorrer CAMBIO_MIN px seguidos en la
    // misma dirección, medidos desde donde cambió. Antes bastaba un delta de
    // 10px entre cuadros, y el rebote del scroll o un temblor del dedo los
    // hacía aparecer y desaparecer solos.
    const CAMBIO_MIN = 40;
    let direccion = 0;
    let ancla = lastY;

    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const currentY = getY();

        setShowBackToTop(currentY > 800);

        // Progreso del viraje a blanco: cada cuadro, para que el fade siga al
        // dedo sin saltos en scrolls cortos.
        const sentinel = categoriesSentinelRef.current;
        if (sentinel) {
          const falta = sentinel.getBoundingClientRect().top - fixedHeaderHeight;
          const p = Math.min(1, Math.max(0, (WHITE_FADE_RANGE - falta) / WHITE_FADE_RANGE));
          setSearchWhite(p);
        }

        // También cada cuadro: un scroll corto justo en el punto de anclaje
        // dejaría el estado viejo.
        setHeaderStuck(currentY >= headerCollapseRef.current - 1 && headerCollapseRef.current > 0);

        // Dirección actual; al cambiar, el recorrido se cuenta desde ahí.
        const d = Math.sign(currentY - lastY);
        if (d !== 0 && d !== direccion) {
          direccion = d;
          ancla = lastY;
        }
        lastY = currentY;
        const recorrido = Math.abs(currentY - ancla);

        const stuckAt = stuckAtYRef.current;
        if (stuckAt == null) {
          // Sin pegar: los filtros se ven.
          setFiltersHidden(false);
        } else if (direccion < 0 && recorrido > CAMBIO_MIN) {
          // Subiendo de verdad: vuelven.
          setFiltersHidden(false);
        } else if (direccion > 0 && recorrido > CAMBIO_MIN && currentY > stuckAt + HIDE_FILTERS_AFTER) {
          setFiltersHidden(true);
        }

        rafId = null;
      });
    };

    const target = container || window;
    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [scrollContainerRef, fixedHeaderHeight]);

  useEffect(() => {
    const handler = (e) => setCartBarVisible(!!e.detail?.visible);
    window.addEventListener('homeCartBarVisibility', handler);
    return () => window.removeEventListener('homeCartBarVisibility', handler);
  }, []);

  return {
    categoriesStuck,
    filtersStuck,
    searchWhite,
    filtersHidden: filtersStuck && filtersHidden,
    headerCollapse,
    headerStuck,
    fixedHeaderHeight,
    categoriesBarHeight,
    showBackToTop,
    cartBarVisible,
    refs: { headerRef, searchRowRef, categoriesSentinelRef, filtersSentinelRef, categoriesBarRef },
  };
}
